<!--
  International Auctions — partner auction house page (BXS-3627 F1.2).

  Built-in control flow only: there is not one `<ng-container>` or
  `<ng-template>` in this file, so `npm run lint:templates` (the BXS-3584 guard)
  has nothing to catch. The house's brand colour is DATA — it reaches CSS as the
  `--house-brand` / `--house-ink` custom properties on the real `<div>` below,
  never as a hardcoded hex and never on a container node.

  Three states, in strict order: loading, then not-found, then the page. The
  not-found copy can only be reached once the house request has actually
  answered.
-->

@if (houseLoading) {
  <!-- Loading. Shaped like the real page so nothing jumps when it resolves. -->
  <div class="ia-house ia-house--loading" role="status" aria-live="polite">
    <span class="ia-house__sr">{{ 'common.loading' | translate }}</span>

    <div class="ia-house__band ia-house__band--ghost" aria-hidden="true">
      <div class="ia-house__band-inner">
        <span class="ia-house__skeleton ia-house__skeleton--plaque"></span>
        <span class="ia-house__skeleton ia-house__skeleton--title"></span>
        <span class="ia-house__skeleton ia-house__skeleton--line"></span>
      </div>
    </div>

    <div class="ia-house__section" aria-hidden="true">
      <div class="ia-house__inner">
        <div class="ia-house__grid">
          @for (card of skeletonCards; track card) {
            <span class="ia-house__skeleton ia-house__skeleton--card"></span>
          }
        </div>
      </div>
    </div>
  </div>
} @else if (!house) {
  <!-- Unknown slug. `getHouse()` answers `null`; it never throws, so this is a
       designed destination and not an error page. -->
  <div class="ia-house ia-house--missing">
    <section class="ia-house__missing">
      <div class="ia-house__inner">
        <div class="ia-house__missing-body">
          <p class="ia-house__eyebrow">
            {{ 'international_auctions.eyebrow' | translate }}
          </p>
          <h1 class="ia-house__missing-title">
            {{ 'international_auctions.empty.house_not_found' | translate }}
          </h1>
          <p class="ia-house__missing-hint">
            {{ 'international_auctions.empty.house_not_found_hint' | translate }}
          </p>
          <a
            class="ia-house__missing-link"
            [routerLink]="indexLink | localeLink"
          >
            <span class="ia-house__arrow" aria-hidden="true">&#8592;</span>
            <span>{{ 'international_auctions.back_to_index' | translate }}</span>
          </a>
        </div>
      </div>
    </section>
  </div>
} @else {
  <div
    class="ia-house"
    [style.--ia-brand]="brand.color"
    [style.--ia-ink]="brand.ink"
    [style.--ia-accent]="brand.accent"
    [style.--ia-accent-ink]="brand.accentInk"
  >
    <!-- ---------------------------------------------------------------- -->
    <!-- Co-branded band: the partner's colour, the partner's logo.        -->
    <!-- ---------------------------------------------------------------- -->
    <header class="ia-house__band">
      <div class="ia-house__band-inner">
        <a class="ia-house__back" [routerLink]="indexLink | localeLink">
          <span class="ia-house__arrow" aria-hidden="true">&#8592;</span>
          <span>{{ 'international_auctions.back_to_index' | translate }}</span>
        </a>

        <div class="ia-house__identity">
          <span class="ia-house__plaque" [class.is-bare]="brand.logoOnBrand">
            @if (hasLogo) {
              <img
                class="ia-house__logo"
                [src]="logoUrl"
                [alt]="houseName"
                decoding="async"
              />
            } @else {
              <span class="ia-house__wordmark">{{ houseName }}</span>
            }
          </span>

          <div class="ia-house__naming">
            <h1 class="ia-house__name">{{ houseName }}</h1>
            @if (countryLabel) {
              <p class="ia-house__country">{{ countryLabel }}</p>
            }
          </div>
        </div>

        @if (websiteUrl) {
          <a
            class="ia-house__website"
            [href]="websiteUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span>{{
              'international_auctions.house.visit_website'
                | translate: { house: houseName }
            }}</span>
            <span class="ia-house__arrow" aria-hidden="true">&#8599;</span>
          </a>
        }
      </div>
    </header>

    <!-- ---------------------------------------------------------------- -->
    <!-- Profile: the blurb, plus the two facts a partner checks first.    -->
    <!-- ---------------------------------------------------------------- -->
    <section class="ia-house__profile">
      <div class="ia-house__inner ia-house__profile-inner">
        <div class="ia-house__about">
          <p class="ia-house__eyebrow">
            {{
              'international_auctions.house.about'
                | translate: { house: houseName }
            }}
          </p>
          <!-- Never guarded. A house with English only (Arqana) must render its
               English paragraph on the Arabic site, not a blank block.
               `dir="auto"` because the paragraph's language is NOT the page's:
               the fallback can hand an English blurb to an Arabic page, and
               without this the browser would lay that English out right-to-
               left and strand its full stop at the wrong end of the line. A
               static attribute, so the container-binding guard is untouched. -->
          <p class="ia-house__blurb" dir="auto">{{ houseDescription }}</p>
        </div>

        <dl class="ia-house__facts">
          @if (venueLabel) {
            <div class="ia-house__fact">
              <dt class="ia-house__fact-label">
                {{ 'international_auctions.house.based_at' | translate }}
              </dt>
              <dd class="ia-house__fact-value">{{ venueLabel }}</dd>
            </div>
          }
          @if (countryLabel) {
            <div class="ia-house__fact">
              <dt class="ia-house__fact-label">
                {{ 'international_auctions.house.country' | translate }}
              </dt>
              <dd class="ia-house__fact-value">{{ countryLabel }}</dd>
            </div>
          }
        </dl>
      </div>
    </section>

    <!-- ---------------------------------------------------------------- -->
    <!-- Every sale this house has with us, filterable by status.          -->
    <!-- ---------------------------------------------------------------- -->
    <section class="ia-house__section">
      <div class="ia-house__inner">
        <div class="ia-house__section-head">
          <h2 class="ia-house__section-title">
            {{ 'international_auctions.house.sales_heading' | translate }}
          </h2>
          @if (!salesLoading) {
            <span class="ia-house__count">{{
              salesCountKey | translate: { count: sales.length }
            }}</span>
          }
        </div>

        <div class="ia-house__filters">
          <span class="ia-house__filter-label" id="ia-house-status-label">
            {{ 'international_auctions.filters.status' | translate }}
          </span>
          <div
            class="ia-house__pills"
            role="group"
            aria-labelledby="ia-house-status-label"
          >
            @for (option of statusFilters; track option.labelKey) {
              <button
                type="button"
                class="ia-house__pill"
                [class.is-active]="isStatusActive(option.value)"
                [attr.aria-pressed]="isStatusActive(option.value)"
                (click)="selectStatus(option.value)"
              >
                {{ option.labelKey | translate }}
              </button>
            }
          </div>
        </div>

        @if (salesLoading) {
          <div class="ia-house__grid" role="status" aria-live="polite">
            <span class="ia-house__sr">{{ 'common.loading' | translate }}</span>
            @for (card of skeletonCards; track card) {
              <span
                class="ia-house__skeleton ia-house__skeleton--card"
                aria-hidden="true"
              ></span>
            }
          </div>
        } @else if (hasSales) {
          <div class="ia-house__grid">
            @for (s of sales; track s.id) {
              <app-international-sale-card
                [sale]="s"
                variant="grid"
              ></app-international-sale-card>
            }
          </div>
        } @else {
          <p class="ia-house__empty">
            {{ 'international_auctions.empty.sales' | translate }}
          </p>
        }

        <p class="ia-house__disclaimer">
          <span class="ia-house__mark" aria-hidden="true">&#10005;</span>
          <span>{{ 'international_auctions.disclaimer' | translate }}</span>
        </p>
      </div>
    </section>
  </div>
}
